import { FC, useState, useCallback } from 'react'
import { Select } from 'antd'
import { Employee, EmployeeNameSearchInfo } from '../../../types'
import { getEmployeesByNameSearch } from './util'
import { LabeledValue } from 'antd/lib/select'
import PerText from '../Text'
import { SmartAvatar } from '../../../modules/avatar/SmartAvatar'
import { employeeSorter } from '../../../shared/sortUtils'
import { useAuth } from '../../../context/auth-context'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
const { Option } = Select

interface EmployeeSearchSelectProps {
  selectedValue?: EmployeeNameSearchInfo
  onSelectValue: (value: EmployeeNameSearchInfo) => void
  notFoundContentText: string
  placeholderText: string
}

export const EmployeeSearchSelect: FC<EmployeeSearchSelectProps> = ({
  selectedValue,
  onSelectValue,
  notFoundContentText,
  placeholderText,
}) => {
  const [timer, setTimer] = useState(-1)
  const [fetching, setFetching] = useState(false)
  const [employeeData, setEmployeeData] = useState<EmployeeNameSearchInfo[]>([])
  const { user } = useAuth()

  // all *Extend*-functions manage handling for HR User search bar in EmployeeSearchSelect
  const onExtendedEmployeeUpdate = (value: EmployeeNameSearchInfo[]) => {
    setEmployeeData(value)
    setFetching(false)
  }

  const loadExtendedEmployeeOptionsCallback = useCallback((value: EmployeeNameSearchInfo[]) => {
    onExtendedEmployeeUpdate(sortExtendedEmployeeByName(value))
  }, [])

  const sortExtendedEmployeeByName = (employeeData: EmployeeNameSearchInfo[]) => {
    return [...employeeData].sort((a, b) => {
      let firstNameOfA: string
      let firstNameOfB: string
      a.chosenFirstName ? (firstNameOfA = a.chosenFirstName) : (firstNameOfA = a.legalFirstName)
      b.chosenFirstName ? (firstNameOfB = b.chosenFirstName) : (firstNameOfB = b.legalFirstName)
      return firstNameOfA.localeCompare(firstNameOfB)
    })
  }

  const onExtendedSearch = async (searchString: string) => {
    setFetching(true)
    if (searchString.length > 3) {
      clearTimeout(timer) // clear timeout from below, if you are a fast typer
      await getEmployeesByNameSearch(loadExtendedEmployeeOptionsCallback, searchString, 15)
    } else if (searchString.length <= 3) {
      // case: small name like 'Eva' or 'Max' and no further typing
      clearTimeout(timer) // prevent creating dozens of timers every type action in between 1 to 3 characters
      setTimer(
        window.setTimeout(async () => {
          await getEmployeesByNameSearch(loadExtendedEmployeeOptionsCallback, searchString, 15)
          setTimer(-1)
        }, 2000),
      )
    }
  }

  const onExtendedChange = (value: LabeledValue) => {
    const selectedEmployee = employeeData.find((e) => e.id === value.key)
    if (selectedEmployee) {
      onSelectValue(selectedEmployee)
    }
    setFetching(false)
  }

  return (
    <Select
      showSearch
      loading={fetching}
      value={selectedValue ? ({ key: selectedValue.id, label: selectedValue.id } as LabeledValue) : undefined}
      labelInValue
      notFoundContent={notFoundContentText}
      style={{ width: '100%' }}
      placeholder={placeholderText}
      onChange={(value: LabeledValue) => {
        onExtendedChange(value)
      }}
      onSearch={(value: string) => onExtendedSearch(value)}
      filterOption={false}
    >
      {employeeData
        .sort((employeeA, employeeB) =>
          employeeSorter(user.nameDisplayPreference, employeeA as unknown as Employee, employeeB as unknown as Employee),
        )
        .map((employee) => (
          <Option key={`employee-${employee.id}`} value={employee.id}>
            <SmartAvatar firstName={employee.legalFirstName} lastName={employee.legalLastName} employeeId={employee.id} size="small" />
            <PerText display="inline" ml="xxs">
              {computeFullname(
                user.nameDisplayPreference,
                employee.chosenFirstName ? employee.chosenFirstName : employee.legalFirstName,
                employee.chosenLastName ? employee.chosenLastName : employee.legalLastName,
              )}
            </PerText>
            {(employee.chosenFirstName || employee.chosenLastName) && (
              <PerText appearance="label-small" ml="xxs">
                {'(' + computeFullname(user.nameDisplayPreference, employee.legalFirstName, employee.legalLastName) + ')'}
              </PerText>
            )}
            <PerText appearance="label-small" display="inline" mx="xxs">
              |
            </PerText>
            <PerText appearance="label-small" display="inline">
              {employee.buName + ' (' + employee.companyName + ')'}
            </PerText>
          </Option>
        ))}
    </Select>
  )
}
